import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import MuiLink from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DialogConfirm from './DialogConfirm'
import ExtendDeadlineButton from './ExtendDeadlineButton'
import Timestamp from './Timestamp'
import useTasks, { type ProvisionedTask } from '../models/useTasks'
import useBilling from '../models/useBilling'
import { useNoticeLeadMinutes } from '../models/useDeadlineNotify'
import { deadlineNotice } from '../utilities/deadlineNotice'
import { useOrgRole } from '../hooks/useOrgRole'
import {
  ENDED_WINDOW_DAYS,
  compareTasks,
  deadlineSeverity,
  formatDuration,
  nodePageHref,
  taskLifecycle,
  taskShapeLabel,
  taskStatus,
  taskStatusColor,
  taskStatusLabel,
  type TaskLifecycle,
} from '../utilities/taskDeadline'

/**
 * The provisioned-task list — every machine we run for this org and how close each is to the
 * deadline that stops it (the app half of router#968).
 *
 * The deadline was enforceable from the day it shipped and visible nowhere: `provision_node`
 * states it once in its reply, the sweeper writes NO audit row when it acts, and the usage screen
 * above aggregates tasks by machine class, so a customer could not name which container was about
 * to stop or when. This is that screen.
 */

// The clock only has to move as fast as the smallest unit on screen, and the rows below print
// whole minutes. A faster tick would re-render the table for a number that did not change.
const TICK_MS = 30_000

// How often the rows are re-read while a machine is still running. Comfortably finer than the
// router's 10-minute compute-stop sweep, so a stop lands on screen within one sweep of happening.
const REFRESH_MS = 120_000

export default function ProvisionedTasks() {
  // ⚠ `stopping` is the node page's `task_stop`. Terminate here is disabled while it runs, so `deprovision_node` cannot
  // run on a task that a Stop is still closing.
  const { tasks, classes, deprovisioning, deadlineChanging, stopping, error, deprovision, setDeadline, get: refresh } = useTasks()
  const { controls } = useOrgRole()
  // ⚠ Stop, extend and terminate belong to the NODE's controllers: its owner, or an org Owner/Admin
  // (app#373, docs/PERMISSIONS.md § 5 Node). It was `node.write`, so every Member saw the controls
  // of every machine and clicked into the router's refusal. ⚠ The owner comes with the task
  // (`controlOwnerId`), not from the node store: the store does not hold a node that the offline
  // filter hides, and a task that has no node yet belongs to the user who provisioned it.
  // ⚠ The role `controls` asks is the ACTIVE org's, and that is the node's tenant here: this table
  // holds only rows whose tenant (`nodes.owner_org_id`, or the network's org for a task with no
  // node) is the active org (`mergeTenantTasks`, app#385). Benoit 2026-09-26: the node's owner, or
  // an Owner/Admin of the node's org.
  const canStop = (task: ProvisionedTask): boolean => controls(task.controlOwnerId)
  // ⚠⚠ READ OFF THE PLAN ROW, never a list of plan names kept here. `max_hours: 0`
  // is refused on Free, and this is the same column the router decides on — a
  // second list is how a plan added later gets a different answer on each of the
  // two surfaces. ⚠ `=== true`: absent (a deployment predating router `0152`)
  // must mean not offered, which is the direction whose only cost is a missing
  // menu entry rather than a control that can only ever be refused.
  const allowUnlimited = useBilling(s => s.plan?.computeDeadlineOptional) === true

  const [now, setNow] = useState(() => new Date())
  // The viewer's saved lead time, else 60 minutes (nobgp/app#354).
  const leadMinutes = useNoticeLeadMinutes()

  const rows = useMemo(
    () =>
      tasks
        .map(task => ({ task, lifecycle: taskLifecycle(task, now) }))
        .sort((a, b) => compareTasks(a.lifecycle, b.lifecycle)),
    [tasks, now],
  )

  const live = rows.some(r => r.lifecycle.state !== 'ended')

  // ⚠⚠ THE CLOCK IS NOT GATED ON `live`, AND THE TEMPTING GATE IS WRONG. A list of finished rows
  // does NOT say the same thing at every tick: `Started N ago` is rendered from `now` on every row,
  // and a terminated row's `Terminated N ago` is too. Stopping the clock with the last running
  // machine freezes both — the row that read "Terminated under a minute ago" when the sweeper
  // caught it still reads that an hour later, for as long as the tab stays open. The NETWORK poll
  // below is the one that costs anything, and that one is gated.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), TICK_MS)
    return () => clearInterval(id)
  }, [])

  // ⚠⚠ THE ROW THIS SCREEN EXISTS FOR IS THE ONE THAT WOULD GO STALE. `tasks` is not published to
  // realtime, so nothing pushes a stop to an open page, and `get()` otherwise runs only on mount
  // and on an org switch. Without this a machine that passes its deadline counts upward for ever
  // — the router's sweeper ends it within about ten minutes and the screen never finds out. The
  // poll stops with the last running machine, so a page of finished rows costs no requests. ⚠ That
  // gate belongs HERE and not on the display clock above: a finished row has nothing left to fetch,
  // but it still prints two durations that move.
  useEffect(() => {
    if (!live) return
    const id = setInterval(() => void refresh(), REFRESH_MS)
    return () => clearInterval(id)
  }, [live, refresh])

  // ⚠ Renders nothing when the org has never provisioned anything, the same rule ComputeUsage
  // applies: an empty table is a worse answer than no table. Most orgs run no compute at all.
  if (rows.length === 0) return null

  const running = rows.filter(r => r.lifecycle.state !== 'ended').length
  // ⚠ Open machines only. A machine that stopped at its limit is marked on its own row, so a week
  // of stopped machines does not fill the top of the list with notices.
  const soon = rows
    .map(r => ({ ...r, notice: deadlineNotice(r.lifecycle, r.task.stopReason, leadMinutes) }))
    .filter(r => r.notice !== null && r.notice.kind !== 'stopped-at-limit')

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 1 }}>
        <Typography variant="subtitle2">Provisioned machines</Typography>
        {running > 0 && <Chip size="small" label={`${running} running`} variant="outlined" color="success" />}
      </Stack>

      {/* `ch` and not a pixel width: the measure must stay near 75 characters at whatever size the
          caption renders at, and a fixed width drifts off it as soon as the font does. */}
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, maxWidth: '78ch' }}>
        {/* ⚠⚠ THE DISK GOES, AND THE COPY MUST SAY SO. `SuspendProvisionedTask` calls the same
            `ecs.StopTask` a deprovision does, and the task definition mounts no volume — so every
            byte on the container's own filesystem is destroyed at the deadline. Node storage, the
            name, the labels and the role grants survive, which is what makes a re-provision a
            resume. ⚠ `docs/PRICING.md` § Compute AGREES, and has since `4972906c` (2026-09-01) —
            it says a stopped machine keeps "the files in the node's storage", which is this
            caption's "its node storage" in other words. The note that used to sit here, saying
            PRICING.md promised the machine "keeps its files", outlived its own defect and sent a
            reader back to re-fix a correct file.
            ⚠⚠ DO NOT RENAME THE SURFACE HERE YET. nobgp-router#1248 PROPOSES calling it "noBGP
            storage" and is OPEN — PRICING.md carries no such term today (the phrase appears in
            the router repo only in `scripts/stripe-setup.sh`, as a Stripe meter display name).
            Change this caption when #1248 lands, not before: writing down a rename that has not
            happened is the same defect this note replaced. */}
        A machine is terminated at its deadline. Its container and everything on the container's disk
        are then deleted. The node keeps its name, its permissions and its node storage, so you can
        provision the same name again to get the machine back. To KEEP a machine that is still
        working, use Give more time — it moves the deadline and deletes nothing. Open a machine by
        its name to reach its files, services and terminal. Machines terminated in the last{' '}
        {ENDED_WINDOW_DAYS} days stay on this list.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {soon.map(({ task, lifecycle, notice }) => (
        <Alert
          key={task.id}
          severity={notice?.severity}
          sx={{ mb: 1, alignItems: 'center' }}
          action={
            <ExtendDeadlineButton
              lifecycle={lifecycle}
              now={now}
              allowUnlimited={allowUnlimited}
              disabled={!canStop(task) || deadlineChanging !== null}
              busy={deadlineChanging === task.id}
              onPick={hours => void setDeadline(task.id, hours)}
            />
          }
        >
          {task.nodeName}: {notice?.text}
        </Alert>
      ))}

      {/* ⚠ A TABLE IN A DIALOG NEEDS ITS OWN HORIZONTAL SCROLL — `TableContainer` is that scroll. At phone width this panel's
          paper is about 310 px and the table's own columns want more; without this the row
          simply overflows the card and the last column is clipped, with no way to reach it. */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Machine</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Network</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Started</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Deadline</TableCell>
              <TableCell align="right">{/* stop */}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ task, lifecycle }) => (
              <TableRow key={task.id}>
                <TableCell>
                  <MachineCell task={task} />
                </TableCell>
                <TableCell>
                  {/* ⚠ The one column a reader of an EC2 or Docker Desktop list looks at
                      first, and the reason `Starting` and `Terminated` are states here
                      rather than footnotes under the name. */}
                  <Chip
                    size="small"
                    variant="outlined"
                    color={taskStatusColor[taskStatus(task, lifecycle)]}
                    label={taskStatusLabel[taskStatus(task, lifecycle)]}
                  />
                  {deadlineNotice(lifecycle, task.stopReason, leadMinutes)?.kind === 'stopped-at-limit' && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Stopped at its time limit
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {/* ⚠ A link only to a network of the active org (app#385): a node of this org can
                      sit in another org's network, and the app has no page for that network here. */}
                  {task.networkInOrg ? (
                    <MuiLink component={Link} to={`/networks/${task.networkId}`} variant="body2" underline="hover">
                      {task.networkName}
                    </MuiLink>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {task.networkName || (task.networkId ? 'Network of another org' : 'No network')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{taskShapeLabel(task, classes)}</Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={<Timestamp date={new Date(task.createdAt)} variant="full" />}>
                    <Typography variant="body2">{formatDuration(now.getTime() - Date.parse(task.createdAt))} ago</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <DeadlineCell lifecycle={lifecycle} now={now} />
                </TableCell>
                <TableCell align="right">
                  {lifecycle.state !== 'ended' && (
                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                      <ExtendDeadlineButton
                        lifecycle={lifecycle}
                        now={now}
                        allowUnlimited={allowUnlimited}
                        disabled={!canStop(task) || deadlineChanging !== null}
                        busy={deadlineChanging === task.id}
                        onPick={hours => void setDeadline(task.id, hours)}
                      />
                      <DialogConfirm
                        color="error"
                        action="Terminate"
                        title={`Terminate ${task.nodeName}?`}
                        disabled={!canStop(task) || deprovisioning !== null || stopping !== null}
                        dialogChildren={
                          <Typography variant="body1" component="div">
                            This terminates the container now and stops its billing. Everything on the
                            container's disk is deleted. The node keeps its name, its permissions and
                            its node storage, so you can provision the same name again to get the
                            machine back. This can take up to 35 seconds.
                          </Typography>
                        }
                        onConfirm={() => void deprovision(task.id)}
                      >
                        <Button size="small" color="error" disabled={!canStop(task) || deprovisioning !== null || stopping !== null}>
                          {deprovisioning === task.id ? 'Terminating…' : 'Terminate'}
                        </Button>
                      </DialogConfirm>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

/**
 * The machine's name, linked to the node page where there is a node to link to.
 *
 * ⚠⚠ THE LINK IS THE POINT OF THIS COLUMN: a task is a handle, and the machine it names is what a
 * reader wants to open — its files, its services, its terminal. Four states, and only the first
 * has a page: the task has registered and its node is live; the node is soft-deleted (the normal
 * state of an ended task, and its page would 404); the container has not connected yet, so there
 * is no node id at all and the name is the provision-time copy; and the node exists but this reader
 * cannot READ it — see `nodeReadable`.
 *
 * ⚠⚠ LINKABILITY COMES FROM `nodeReadable` AND NEVER FROM `nodeDeleted` ALONE. `nodeDeleted` is
 * derived from a column that is only legible when the node row came back at all, so an unreadable
 * node reads as a live one and earns a link to a page that cannot load.
 */
function MachineCell({ task }: Readonly<{ task: ProvisionedTask }>) {
  // ⚠ The two notes that used to sit here — "starting" and "removed from the
  // directory" — are the Status column's now. What is left is the one thing the
  // status cannot say: that there is no page to open, and why.
  const note =
    task.nodeId === null
      ? 'no node page until it connects'
      : !task.nodeReadable
        ? 'node not visible to you — the name is the one it was provisioned under'
        : task.nodeDeleted
          ? 'node removed'
          : null

  const href = nodePageHref(task)

  return (
    <>
      {href ? (
        <MuiLink component={Link} to={href} variant="body2" underline="hover">
          {task.nodeName}
        </MuiLink>
      ) : (
        <Typography variant="body2">{task.nodeName}</Typography>
      )}
      {note && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {note}
        </Typography>
      )}
    </>
  )
}

/**
 * How far along this machine is toward the deadline that stops it.
 *
 * ⚠⚠ NO DEADLINE IS NOT AN EMPTY BAR. A task with a null `deadline_at` was promised it runs until
 * you stop it, and the sweeper skips it — so it gets a sentence and no bar at all. Drawing an
 * empty bar would tell the reader a stop is coming that is not.
 */
function DeadlineCell({ lifecycle, now }: Readonly<{ lifecycle: TaskLifecycle; now: Date }>) {
  const { state, deadlineAt, fraction, remainingMs, endedAt, endedAtOrAfterDeadline } = lifecycle

  if (state === 'ended') {
    return (
      <>
        <Typography variant="body2">
          Terminated {endedAt ? `${formatDuration(now.getTime() - endedAt.getTime())} ago` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {/* ⚠ A STATEMENT ABOUT TIMESTAMPS, NEVER ABOUT A CAUSE. Nothing records WHY a task
              stopped: the sweeper writes no audit row and stamps the same `completed_at` field a
              `deprovision_node` does. "Was stopped by the deadline" would be a claim the data
              cannot support. */}
          {deadlineAt === null
            ? 'it had no time limit'
            : endedAtOrAfterDeadline
              ? 'it ran to its deadline'
              : `${formatDuration(remainingMs ?? 0)} before its deadline`}
        </Typography>
      </>
    )
  }

  if (deadlineAt === null) {
    return (
      <>
        <Typography variant="body2">No time limit</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          it runs, and bills, until you stop it
        </Typography>
      </>
    )
  }

  const overdue = state === 'expiring'
  return (
    <>
      <LinearProgress
        variant="determinate"
        // ⚠ A null fraction cannot be drawn, and 0 is a lie in the other direction — it would say
        // the machine has just started. The bar goes full and the text below says what is true.
        value={fraction === null ? 100 : fraction * 100}
        color={deadlineSeverity(lifecycle)}
        sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
      />
      <Tooltip title={<Timestamp date={deadlineAt} variant="full" />}>
        <Typography variant="caption" sx={{ color: overdue ? 'error.main' : 'text.secondary' }}>
          {overdue
            ? `${formatDuration(remainingMs ?? 0)} past its deadline`
            : `${formatDuration(remainingMs ?? 0)} left`}
        </Typography>
      </Tooltip>
    </>
  )
}
